import { USER_ROLE } from "enums";
import { useOfflineStorage } from "hooks/useOfflineStorage";
import { useOfflineSync } from "hooks/useOfflineSync";
import { usePWA } from "hooks/usePWA";

interface OfflineStatusCheck {
  canShowDashboard: boolean;
  reason: 'online' | 'admin_role' | 'has_offline_data' | 'no_offline_data' | 'stale_data';
  message?: string;
  stats: {
    lastSyncTime: Date | null;
  };
}

export const useOfflineStatusCheck = (userRole?: string): OfflineStatusCheck => {
  const { isOffline } = usePWA();
  const { getCacheInfo } = useOfflineStorage();
  const { lastSyncTime } = useOfflineSync();
  
  const cacheInfo = getCacheInfo();
  const isFitterOrMeasurer = 
    userRole === USER_ROLE.fitter || 
    userRole === USER_ROLE.measurer;

  const hasOfflineData = cacheInfo.totalEntries > 0;
  const isLastSyncValid = 
    lastSyncTime && 
    Date.now() - lastSyncTime.getTime() < 1 * 24 * 60 * 60 * 1000;
    
  const stats = {
    lastSyncTime
  };

  if (!isOffline) {
    return {
      canShowDashboard: true,
      reason: 'online',
      stats
    };
  }

  if (!isFitterOrMeasurer) {
    return {
      canShowDashboard: true,
      reason: 'admin_role',
      stats
    };
  }

  if (!hasOfflineData) {
    return {
      canShowDashboard: false,
      reason: 'no_offline_data',
      message: 'Aby korzystać z aplikacji w trybie offline, musisz najpierw zsynchronizować dane gdy masz połączenie z internetem.',
      stats
    };
  }

  if (!isLastSyncValid) {
    return {
      canShowDashboard: false,
      reason: 'stale_data',
      message: 'Twoje dane offline są przestarzałe. Połącz się z internetem i zsynchronizuj dane.',
      stats
    };
  }

  return {
    canShowDashboard: true,
    reason: 'has_offline_data',
    stats
  };
};